import React, { useState } from 'react';
import { Menu, Close } from '@material-ui/icons';
import { Stack, Text } from '@solvhealth/jigsaw-v1';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { colors } from '~/constants/colors';
import dataTestIds from '~/test/test-ids';

import {
  everydayHealthLinks,
  urgentCareLinks,
  popularTestLinks,
  womensHealthLinks,
  mobileMenuLinks,
  LinkCategoryList,
  Link,
} from './links';

import {
  HeaderContainer,
  LogoContainer,
  MobileNav,
  MobileMenuContainer,
  StyledList,
  StyledAccordionSubCategoryText,
  MobileLoginButton,
  AppDownloadSection,
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  ShowMobile,
} from './styles';
import AppStoreBadge from '~/components/DownloadDriver/AppStoreBadge';
import {
  HEADER_DIRECTORY_LINKS_CLICK_LOGIN_SIGNUP,
  HEADER_DIRECTORY_LINKS_SHOW_MOBILE_LINK_MENU,
  HEADER_DIRECTORY_LINKS_HIDE_MOBILE_LINK_MENU,
  HEADER_DIRECTORY_LINKS_LINK_CLICK,
  HEADER_DIRECTORY_LINKS_CLICK_LOGO,
  campaignNames,
} from '~/core/analytics/events';
import { analyticsTrackEvent } from '~/core/analytics';

type MobileHeaderProps = {
  mobileMenuOpen: boolean;
  hasScrolled: boolean;
  toggleMobileMenu: () => void;
  openLogInModal: () => void;
};

const SubcategoryAccordion = ({
  category,
  subcategoryLinks,
}: {
  category: string;
  subcategoryLinks: LinkCategoryList;
}) => {
  return (
    <StyledAccordion>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Text fontSize="lg" textColor="gray-600">
          {category}
        </Text>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <Stack>
          {subcategoryLinks.map(({ label: subcategory, links }) => (
            <StyledAccordion key={subcategory}>
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <StyledAccordionSubCategoryText textColor="gray-500">
                  {subcategory}
                </StyledAccordionSubCategoryText>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <StyledList>
                  {links.map((link: Link) => (
                    <li key={link.url}>
                      <a
                        href={link.url}
                        onClick={() =>
                          analyticsTrackEvent(HEADER_DIRECTORY_LINKS_LINK_CLICK, {
                            linkUrl: link.url,
                            linkCategory: category,
                            linkSubcategory: subcategory,
                            label: link.label,
                          })
                        }
                      >
                        <Text fontSize="sm" textColor="crunchberry">
                          {link.richLabel || link.label}
                        </Text>
                      </a>
                    </li>
                  ))}
                </StyledList>
              </StyledAccordionDetails>
            </StyledAccordion>
          ))}
        </Stack>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

const LinkListAccordion = ({ category, links }: { category: string; links: Link[] }) => {
  return (
    <StyledAccordion>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Text fontSize="lg" textColor="gray-600">
          {category}
        </Text>
      </StyledAccordionSummary>

      <StyledAccordionDetails>
        <Stack>
          {links.map((link) => (
            <a
              href={link.url}
              key={link.url}
              onClick={() =>
                analyticsTrackEvent(HEADER_DIRECTORY_LINKS_LINK_CLICK, {
                  linkUrl: link.url,
                  linkCategory: category,
                  label: link.label,
                })
              }
            >
              <Text paddingTop="14px" textColor="crunchberry">
                {link.label}
              </Text>
            </a>
          ))}
        </Stack>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

const MobileHeader = ({
  mobileMenuOpen,
  toggleMobileMenu,
  openLogInModal,
  hasScrolled,
}: MobileHeaderProps) => {
  return (
    <ShowMobile>
      <HeaderContainer>
        <Stack width="100%">
          <Stack
            alignItems="center"
            backgroundColor="white"
            borderBottom={hasScrolled && mobileMenuOpen ? `1px solid ${colors.gray200}` : 'none'}
            justifyContent="space-between"
            padding="1rem 0.5rem"
            position="sticky"
            row
            top="0"
            width="auto"
            zIndex="8000"
          >
            <LogoContainer
              onClick={() => {
                analyticsTrackEvent(HEADER_DIRECTORY_LINKS_CLICK_LOGO);
              }}
            />
            <MobileNav>
              <Stack alignItems="center" paddingRight="12px" row>
                <MobileLoginButton
                  data-testid={dataTestIds.login.mobileBtn}
                  onClick={() => {
                    openLogInModal();
                    analyticsTrackEvent(HEADER_DIRECTORY_LINKS_CLICK_LOGIN_SIGNUP);
                  }}
                  variant="outlined"
                >
                  Log in
                </MobileLoginButton>
                {!mobileMenuOpen && (
                  <Menu
                    onClick={() => {
                      toggleMobileMenu();
                      analyticsTrackEvent(HEADER_DIRECTORY_LINKS_SHOW_MOBILE_LINK_MENU);
                    }}
                  />
                )}
                {mobileMenuOpen && (
                  <Close
                    onClick={() => {
                      toggleMobileMenu();
                      analyticsTrackEvent(HEADER_DIRECTORY_LINKS_HIDE_MOBILE_LINK_MENU);
                    }}
                  />
                )}
              </Stack>
            </MobileNav>
          </Stack>
          {mobileMenuOpen && (
            <MobileMenuContainer>
              <Stack paddingX="24px" paddingY="16px">
                <Text fontSize="sm" fontWeight="bold" marginBottom="12px">
                  BROWSE SERVICES
                </Text>
                <Stack marginLeft="12px">
                  <SubcategoryAccordion category="Urgent care" subcategoryLinks={urgentCareLinks} />
                  <SubcategoryAccordion
                    category="Everyday health"
                    subcategoryLinks={everydayHealthLinks}
                  />
                  <LinkListAccordion category={"Women's health"} links={womensHealthLinks} />

                  <LinkListAccordion category="Popular tests" links={popularTestLinks} />
                </Stack>

                <Text
                  borderTop={`1px solid ${colors.gray200}`}
                  fontSize="sm"
                  fontWeight="bold"
                  margin-bottom="12px"
                  marginTop="16px"
                  paddingTop="32px"
                >
                  FOR PROVIDERS
                </Text>
                <Stack marginLeft="12px">
                  {mobileMenuLinks.map(({ label, url }) => (
                    <a
                      href={url}
                      key={label}
                      onClick={() =>
                        analyticsTrackEvent(HEADER_DIRECTORY_LINKS_LINK_CLICK, {
                          linkUrl: url,
                          linkCategory: 'For providers',
                          label,
                        })
                      }
                    >
                      <Text fontSize="lg" key={label} paddingY="16px" textColor="gray-600">
                        {label}
                      </Text>
                    </a>
                  ))}
                </Stack>
              </Stack>
              <AppDownloadSection>
                <Text fontWeight="bold" marginBottom="24px" textColor="gray-600">
                  Get the Solv app on your phone
                </Text>
                <Stack row>
                  <AppStoreBadge campaignName={campaignNames.HEADER_MOBILE} newWindow store="ios" />
                  <AppStoreBadge
                    campaignName={campaignNames.HEADER_MOBILE}
                    newWindow
                    store="android"
                  />
                </Stack>
              </AppDownloadSection>
            </MobileMenuContainer>
          )}
        </Stack>
      </HeaderContainer>
    </ShowMobile>
  );
};

export default MobileHeader;
